import React, { useMemo, useState, Suspense } from 'react';
import { Link } from 'react-router-dom';
import useWeatherNormals from '../../hooks/useWeatherNormals';
import { getRegionColorMap } from '../../lib/regions';
import { trackEvent } from '../../utils/analytics';
import { getRuntimeSiteContext } from '../../lib/siteContext';
import { getDefaultWeatherLocationId, getIslandWeatherLocations } from '../../lib/weatherIslands';

const Weather3DHero = React.lazy(() => import('../weather/Weather3DHero'));

const MAX_SELECTION = 3;
const METRIC_CHOICES = [
  { id: 'temp', detail: 'high', label: 'High Temp' },
  { id: 'rain', detail: 'total', label: 'Rain' },
  { id: 'wind', detail: 'mean', label: 'Wind' },
  { id: 'swell', detail: 'waveHeight', label: 'Swell' },
];

const brightenColor = (hex, lightBoost = 0.35, satBoost = 0.0) => {
  if (!hex || typeof hex !== 'string') return hex;
  const raw = hex.replace('#', '');
  if (raw.length !== 6) return hex;
  const r = parseInt(raw.slice(0, 2), 16) / 255;
  const g = parseInt(raw.slice(2, 4), 16) / 255;
  const b = parseInt(raw.slice(4, 6), 16) / 255;
  if ([r, g, b].some(Number.isNaN)) return hex;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  let l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
    }
    h /= 6;
  }

  s = Math.min(1, s + satBoost);
  l = Math.min(1, l + lightBoost);

  const hue2rgb = (p, q, t) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };

  let rr = l;
  let gg = l;
  let bb = l;
  if (s !== 0) {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    rr = hue2rgb(p, q, h + 1 / 3);
    gg = hue2rgb(p, q, h);
    bb = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (val) => Math.round(val * 255).toString(16).padStart(2, '0');
  return `#${toHex(rr)}${toHex(gg)}${toHex(bb)}`;
};

const WeatherToolPreview = ({
  showIntro = true,
  compact = false,
  analyticsSource = 'homepage_weather_module',
}) => {
  const { island, islandConfig } = React.useMemo(() => getRuntimeSiteContext(), []);
  const { data, loading } = useWeatherNormals();
  const [selectedIds, setSelectedIds] = useState([]);
  const [metricChoice, setMetricChoice] = useState(METRIC_CHOICES[0]);
  const regionColors = useMemo(() => getRegionColorMap(island), [island]);

  const islandLocations = useMemo(
    () => getIslandWeatherLocations(data?.locations, islandConfig.key),
    [data?.locations, islandConfig.key]
  );

  React.useEffect(() => {
    const defaultLocationId = getDefaultWeatherLocationId(data?.locations, islandConfig.key);
    if (!defaultLocationId) return;
    setSelectedIds((prev) => {
      const validIds = prev.filter((id) => islandLocations.some((location) => location.id === id));
      return validIds.length ? validIds : [defaultLocationId];
    });
  }, [data?.locations, islandConfig.key, islandLocations]);

  const selectedLocations = useMemo(() => {
    if (!data?.locations) return [];
    return selectedIds
      .map(id => data.locations.find(loc => loc.id === id))
      .filter(Boolean);
  }, [data, selectedIds]);

  const locationColorMap = useMemo(() => {
    if (!data?.locations) return {};
    const map = {};
    data.locations.forEach((loc) => {
      if (loc.regionLabel && regionColors[loc.regionLabel]) {
        map[loc.id] = brightenColor(regionColors[loc.regionLabel]);
      }
    });
    return map;
  }, [data, regionColors]);

  const handleToggle = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        if (prev.length === 1) return prev;
        return prev.filter(item => item !== id);
      }
      const next = [...prev, id];
      if (next.length <= MAX_SELECTION) return next;
      return [...next.slice(1)];
    });
  };

  const weatherLink = useMemo(() => {
    const params = new URLSearchParams();
    selectedIds.forEach((id, idx) => {
      params.set(String.fromCharCode(97 + idx), id);
    });
    params.set('mode', 'year');
    params.set('metric', metricChoice.id);
    params.set('detail', metricChoice.detail);
    return `/weather?${params.toString()}`;
  }, [selectedIds, metricChoice]);

  return (
    <section className="text-center">
      <div className="max-w-6xl mx-auto">
        {showIntro ? (
          <>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">
              {`${islandConfig.title} Weather, Visualized`}
            </h2>
            <p className="text-lg md:text-xl mb-6 max-w-3xl mx-auto leading-relaxed text-center">
              {`Hawaiʻi’s micro-climates make weather famously local. Use our interactive tool to compare conditions across ${islandConfig.shortTitle} and the rest of Hawaiʻi.`}
            </p>
          </>
        ) : null}

        <div className="flex flex-col gap-6">
          <div className="grid gap-8 lg:grid-cols-[3fr_5fr] items-center text-left order-2 lg:order-3">
            <div className="space-y-4">
              <div className="flex flex-wrap md:flex-nowrap gap-2">
                {METRIC_CHOICES.map((choice) => {
                  const isActive = metricChoice.id === choice.id && metricChoice.detail === choice.detail;
                  return (
                    <button
                      key={choice.id}
                      type="button"
                      onClick={() => setMetricChoice(choice)}
                      className={`rounded-full border px-3 py-1.5 text-[10px] sm:text-[11px] uppercase tracking-[0.2em] whitespace-nowrap transition ${
                        isActive ? 'border-white/50 bg-white/15 text-white' : 'border-white/15 bg-white/5 text-white/60 hover:bg-white/10'
                      }`}
                    >
                      {choice.label}
                    </button>
                  );
                })}
              </div>
              <div className="grid gap-3">
                {islandLocations.map((loc) => {
                  const isSelected = selectedIds.includes(loc.id);
                  const color = locationColorMap[loc.id] || 'rgba(255,255,255,0.35)';
                  const label = loc.regionLabel || loc.label;
                  return (
                    <button
                      key={loc.id}
                      type="button"
                      onClick={() => handleToggle(loc.id)}
                      className={`flex items-center justify-between rounded-lg border px-4 py-3 transition ${
                        isSelected
                          ? 'border-white/40 bg-white/10'
                          : 'border-white/15 bg-white/5 hover:bg-white/10'
                      }`}
                    >
                      <span className="flex items-center gap-3 text-white">
                        <span
                          className="h-4 w-4 rounded-full border border-white/30 shadow-[0_0_10px_rgba(255,255,255,0.15)]"
                          style={{ backgroundColor: color, opacity: isSelected ? 1 : 0.65 }}
                        />
                        <span className="font-semibold">{label}</span>
                      </span>
                      <span className={`text-[10px] uppercase tracking-[0.3em] ${isSelected ? 'text-white/70' : 'text-white/40'}`}>
                        {isSelected ? 'Selected' : 'Add'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="relative">
              <div className="absolute top-0 right-0 z-10 h-16 w-16 overflow-hidden pointer-events-none sm:h-20 sm:w-20">
                <div className="absolute top-3 right-[-36px] w-[140px] rotate-45 bg-white/20 text-white text-[9px] sm:text-[10px] uppercase tracking-[0.25em] py-1 text-center shadow-md">
                  Preview
                </div>
              </div>
              {loading ? (
                <div className={`${compact ? 'h-[300px] sm:h-[340px] lg:h-[390px]' : 'h-[320px] sm:h-[380px] lg:h-[470px]'} rounded-lg border border-white/15 bg-white/5 backdrop-blur flex items-center justify-center`}>
                  <div className="text-center text-white/70 text-sm">Loading visualization…</div>
                </div>
              ) : (
                <Suspense
                  fallback={(
                    <div className={`${compact ? 'h-[300px] sm:h-[340px] lg:h-[390px]' : 'h-[320px] sm:h-[380px] lg:h-[470px]'} rounded-lg border border-white/15 bg-white/5 backdrop-blur flex items-center justify-center`}>
                      <div className="text-center text-white/70 text-sm">Loading visualization…</div>
                    </div>
                  )}
                >
                  <Weather3DHero
                    data={data}
                    scales={data?.scales}
                    selectedLocations={selectedLocations}
                    mode="year"
                    metric={metricChoice.id}
                    metricDetail={metricChoice.detail}
                    metricDetailOptions={[]}
                    unitSystem="imperial"
                    showHeader={false}
                    showLegend={false}
                    showDetailSelect={false}
                    enablePointer={false}
                    allowRotate={false}
                    autoRotate={true}
                    containerClassName={compact
                      ? 'h-[300px] sm:h-[340px] lg:h-[390px]'
                      : 'h-[320px] sm:h-[380px] lg:h-[470px]'}
                    locationColorMap={locationColorMap}
                  />
                </Suspense>
              )}
            </div>
          </div>

          <div className="order-3 lg:order-2 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to={weatherLink}
              onClick={() =>
                trackEvent('weather_tool_click', {
                  source: analyticsSource,
                  cta: 'compare_weather',
                })
              }
              className="btn-secondary !flex w-full sm:w-auto justify-center text-base md:text-lg text-center"
            >
              Weather Comparison Tools →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WeatherToolPreview;
